// Templates › Signature and footer: what every email gets after a template's
// words (owner, 2026-11-03: "include that as a section in the template builder,
// so we can update that and change it"). One setting for every template, so a
// change here changes every email from then on: the one-off signature, the logo
// (which can be turned off), and a campaign email's footer: an optional notice,
// and the unsubscribe link, which can be turned off or reworded (owner, November
// 4: "allow us to edit all aspects ... it should just say unsubscribe link").
// There's no postal address for now (owner, November 4: "Remove all postal
// addresses for now until we have a real address to give"): nothing asks for
// one. One set on the server (GROWTH_MAILING_ADDRESS) is shown here and printed
// in campaign footers. A one-off email to a lead ends at the
// signature, so it has no footer.

import { useState, type FormEvent } from 'react'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { EmailFooter } from '@/api/growthCampaignTypes'
import { Button, Card, CardHeader, ErrorState, FormField, InlineAlert, LoadingState, Textarea, TextInput, Toggle, useToast } from '@/components/ui'
import { invalidateQueries, useQuery } from '@/lib/query'

const SAMPLE_SIGNER = { name: 'Omar', title: 'Co-founder' }
const SAMPLE_LEAGUE = 'Riverside Youth Basketball'

/** The signature with a signer, as the server writes it (signers.ts › signatureBlock). */
function signed(template: string): string {
  return template
    .replace(/\{\{\s*(signer_name|signer_title)\s*(?:\|([^}]*))?\}\}/g, (_, f: string, alt?: string) => (f === 'signer_name' ? SAMPLE_SIGNER.name : SAMPLE_SIGNER.title) || alt?.trim() || '')
    .split('\n')
    .map((l) => l.replace(/^\s*,\s*/, '').trimEnd())
    .join('\n')
    .trim()
}

function noticeFor(notice: string): string {
  return notice.replace(/\{\{\s*league_name\s*(?:\|[^}]*)?\}\}/g, SAMPLE_LEAGUE)
}

function Part({ title, when, children }: { title: string; when: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-1.5 border-t border-line pt-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 className="text-[13px] font-bold text-ink">{title}</h3>
        <span className="text-xs text-muted">{when}</span>
      </div>
      {children}
    </section>
  )
}

function Shown({ footer, mailing }: { footer: EmailFooter; mailing: string | null }) {
  const signature = signed(footer.signature)
  return (
    <div className="grid gap-3">
      <Part title="Signature" when="One-off emails, signed by whoever sends">
        {signature ? (
          <pre data-testid="footer-signature" className="whitespace-pre-wrap break-words rounded-md bg-raised p-3 font-sans text-[13px] text-ink">
            {signature}
          </pre>
        ) : (
          <p className="text-[13px] text-muted">No signature.</p>
        )}
        <p className="text-xs text-subtle">A campaign email has no signer: sign it in the template.</p>
      </Part>
      <Part title="Logo" when="Under every email">
        {footer.logo_enabled && footer.logo_url ? (
          <div className="rounded-md border border-line bg-white p-3">
            <img data-testid="footer-logo" src={footer.logo_url} alt="ScoreLM" width={footer.logo_width} style={{ width: footer.logo_width, maxWidth: '100%', height: 'auto' }} />
          </div>
        ) : (
          <p className="text-[13px] text-muted">{footer.logo_enabled ? 'No logo.' : 'Logo off.'}</p>
        )}
      </Part>
      <Part title="Campaign footer" when="Under campaign emails">
        {footer.notice.trim() || mailing || footer.unsubscribe_enabled ? (
          <div data-testid="footer-marketing" className="grid gap-0.5 rounded-md bg-raised p-3 text-xs text-muted">
            {footer.notice.trim() ? <span>{noticeFor(footer.notice)}</span> : null}
            {mailing ? <span>{mailing}</span> : null}
            {footer.unsubscribe_enabled ? <span className="underline">{footer.unsubscribe_text}</span> : null}
          </div>
        ) : (
          <p data-testid="footer-marketing" className="text-[13px] text-muted">Nothing: a campaign email ends at its last line.</p>
        )}
        <p className="text-xs text-subtle">
          {footer.unsubscribe_enabled ? null : 'No unsubscribe link. '}
          {mailing ? 'The postal address is added because it is set on the server. ' : null}An email to a lead from its page ends at the signature, with no footer.
        </p>
      </Part>
    </div>
  )
}

function Edit({ footer, defaults, mailing, onDone }: { footer: EmailFooter; defaults: EmailFooter; mailing: string | null; onDone: () => void }) {
  const toast = useToast()
  const [draft, setDraft] = useState({ ...footer, logo_width: String(footer.logo_width) })
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const set = (patch: Partial<typeof draft>) => setDraft((d) => ({ ...d, ...patch }))

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setProblem(null)
    try {
      await api.growth.templates.saveFooter({ ...draft, logo_width: Number(draft.logo_width) })
      invalidateQueries('growth')
      toast.success('Saved. Every email uses it from now on.')
      onDone()
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={(e) => void save(e)} className="grid gap-4" aria-label="Signature and footer">
      <FormField label="Signature" hint="One-off emails. Use {{signer_name}} and {{signer_title}} for whoever sends it. Empty means no signature.">
        {({ id, describedBy }) => <Textarea id={id} aria-describedby={describedBy} rows={4} maxLength={600} value={draft.signature} onChange={(e) => set({ signature: e.target.value })} />}
      </FormField>
      <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
        <div className="flex items-center gap-2">
        <Toggle checked={draft.logo_enabled} onChange={(v) => set({ logo_enabled: v })} label="Show the logo" />
        <span className="text-[13px] text-ink">Show the logo</span>
      </div>
      <FormField label="Logo image" hint="A full https:// link. Empty means no logo.">
          {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} type="url" value={draft.logo_url} placeholder="https://scorelm.com/logo.png" onChange={(e) => set({ logo_url: e.target.value })} />}
        </FormField>
        <FormField label="Width" hint="Pixels.">
          {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} type="number" min={40} max={600} value={draft.logo_width} onChange={(e) => set({ logo_width: e.target.value })} />}
        </FormField>
      </div>
      <FormField label="Logo links to" hint="Empty means the logo is not a link.">
        {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} type="url" value={draft.logo_link} placeholder="https://scorelm.com" onChange={(e) => set({ logo_link: e.target.value })} />}
      </FormField>
      <FormField label="Marketing notice" hint="Optional. A line at the bottom of campaign emails; use {{league_name}}. Empty: no line.">
        {({ id, describedBy }) => <Textarea id={id} aria-describedby={describedBy} rows={2} maxLength={400} value={draft.notice} onChange={(e) => set({ notice: e.target.value })} />}
      </FormField>
      <div className="flex items-center gap-2">
        <Toggle checked={draft.unsubscribe_enabled} onChange={(v) => set({ unsubscribe_enabled: v })} label="Unsubscribe link in campaign emails" />
        <span className="text-[13px] text-ink">Unsubscribe link in campaign emails</span>
      </div>
      <FormField label="Unsubscribe link words" hint="What the link says. Empty means Unsubscribe.">
        {({ id, describedBy }) => (
          <TextInput id={id} aria-describedby={describedBy} maxLength={60} disabled={!draft.unsubscribe_enabled} value={draft.unsubscribe_text} placeholder="Unsubscribe" onChange={(e) => set({ unsubscribe_text: e.target.value })} />
        )}
      </FormField>
      {mailing ? <p className="text-xs text-muted">The postal address ({mailing}) is added under the notice: it's set on the server.</p> : null}
      {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={() => setDraft({ ...defaults, logo_width: String(defaults.logo_width) })}>
          Back to the default
        </Button>
        <div className="flex gap-2">
          <Button onClick={onDone} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            Save for every email
          </Button>
        </div>
      </div>
    </form>
  )
}

export function EmailFooterCard() {
  const { data, error, status, refetch } = useQuery(['growth', 'email-footer'], (signal) => api.growth.templates.footer(signal))
  const [editing, setEditing] = useState(false)
  return (
    <Card>
      <CardHeader
        title="Signature and footer"
        description="Added to every email after the template's words. One setting for all templates: a change here changes every email from now on."
        actions={data && !editing ? <Button size="sm" onClick={() => setEditing(true)}>Edit</Button> : undefined}
      />
      {status === 'loading' && !data ? (
        <LoadingState label="Loading the signature and footer…" />
      ) : !data ? (
        <ErrorState error={error} title="Couldn't load the signature and footer" onRetry={refetch} />
      ) : editing ? (
        <Edit footer={data.footer} defaults={data.defaults} mailing={data.mailing_address} onDone={() => setEditing(false)} />
      ) : (
        <Shown footer={data.footer} mailing={data.mailing_address} />
      )}
    </Card>
  )
}
